import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_one, fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';
import { TreatmentPlan } from '../../../../models/TreatmentPlan';
import { Medication } from '../../../../models/Medication';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

type Props = {
  object: User;
  mutationCustomAction: any;
  consentAsked?: string;
  setPremiumNutriAdviceOpen: (b:boolean) => void;
  setNewEpisodeWarningOpen: (b:boolean) => void;
}

export const CoachUserWidgetsFileSmall: FunctionComponent<Props> = ({
  object,
  mutationCustomAction,
  consentAsked,
  setPremiumNutriAdviceOpen,
  setNewEpisodeWarningOpen
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  // Pre-format dates as strings (Luxon, same output as <Dt />) so static +
  // value can live in ONE Typography string child — required for the html
  // attribute to engage (it only applies to single-string children).
  const fmtDay = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'}) : '';
  }
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  // (No module check needed: the CTA has no premium lock — the file
  // page's tabs gate their own modules.)

  let count = 0;

  // Coach-level widget settings: composite { cta, items } + a sibling order
  // key. Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.filesSmallValueKey];
  const showCta = cfg?.cta !== false; // default on
  const orderedItems = HelpersClientWidgets.getOrderedFilesSmallItems(
    widgetSettings?.[HelpersClientWidgets.filesSmallOrderKey],
    cfg?.items
  );
  const itemState = (key: string) => orderedItems.find((i) => i.key === key)?.state === 'shown';

  // Intake profile completion % (the file page's custom_action). Only fetched
  // when the intake stats row is shown.
  const {
    data: intakeProgress,
    isSuccess: intakeProgressSuccess,
  } = useQuery({
    queryKey: ["intake_progress", object?.id, DateTime.now().toFormat("dd/LL/y")],
    queryFn: () =>
      fetch_one<{custom_result: number}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        {
          object: 'user',
          class_action: 'custom_api_get_intake_progress',
          id: object?.id,
        },
        login
      ),
    enabled: itemState('intake_stats') && !!login && object?.type === "User::User" && !!object?.id,
  });

  // Treatment plan (first/only row) for the updated_at + editor stat. Only
  // fetched when the treatment plan stats row is shown.
  const {
    data: treatmentPlan,
  } = useQuery({
    queryKey: ["treatment_plans", object?.id],
    queryFn: () =>
      fetch_all_with_count<TreatmentPlan>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'treatment_plan',
          fields: ['id', 'updated_at', 'editor|id/full_name'],
          page: 0,
          per_page: 1,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => d.result[0],
    enabled: itemState('treatment_plan_stats') && !!object?.id && !!(login?.practice_id || currentPractice?.id) && object?.type === "User::User",
  });

  // Latest advice upload date ("the latest upload dates from x"). Same order
  // as the uploads tab. Only fetched when the uploads stats row is shown.
  const {
    data: lastUpload,
  } = useQuery({
    queryKey: ["client", "advice_uploads_last", object?.id],
    queryFn: () =>
      fetch_all_with_count<{date?: string}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'blood_upload',
          fields: ['id', 'date'],
          page: 0,
          per_page: 1,
          order: 'date DESC, created_at DESC',
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id),
              for_practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => d.result[0],
    enabled: itemState('uploads_stats') && !!object?.id && !!(login?.practice_id || currentPractice?.id),
  });

  // Timeline stats (ex-msbo): entry count + the last entry's end date. The
  // latest row is ordered med_end-first but falls back to med_start when
  // med_end is blank — replicated client-side by fetching both dates for the
  // two candidate rows (one per order) and picking the max resolved date.
  // `d.count` is the total regardless of per_page. Only fetched when the
  // timeline stats row is shown.
  const {
    data: timelineRows,
  } = useQuery({
    queryKey: ["client", "medications_last", object?.id],
    queryFn: () =>
      fetch_all_with_count<Medication>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'medication',
          fields: ['id', 'med_start', 'med_end'],
          page: 0,
          per_page: 2,
          order: 'med_end DESC',
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => {
        count = d.count;
        return d.result;
      },
    enabled: itemState('timeline_stats') && !!object?.id && !!(login?.practice_id || currentPractice?.id),
  });

  // Resolve the timeline's "last one is on y": med_end unless blank, then
  // med_start. The med_end-DESC row is the obvious candidate, but a row with
  // only med_start can top it when every med_end is blank — hence the
  // best-of-both comparison over the fetched candidates.
  const timelineLastDate = (() => {
    const rows = timelineRows || [];
    if (rows.length === 0) return undefined;
    const resolve = (r?: Medication) => r?.med_end || r?.med_start;
    const best = rows.reduce((acc, r) => {
      const d = resolve(r);
      if (!d) return acc;
      return !acc || DateTime.fromISO(d) > DateTime.fromISO(acc) ? d : acc;
    }, undefined as string | undefined);
    return best;
  })();

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row. Gated by
        the coach's `cta` setting. No premium lock — the file page's tabs gate
        their own modules. */}
    {showCta && <Button
      label={t("coach.general.actions.to_file")}
      id="goto_file"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}/file`, {state: {tab: 0}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.intake_profile")}</Typography>
        <Divider />
      </Grid>

      {/* No intake consent: the fixed ask-consent chrome (warning + ask
          button + limited-version / asked-note). The 7 stat rows below only
          render in the consented state. */}
      {!object.consent_status?.is_intake && <Grid item xs={12}>
        <Typography sx={{color: theme.palette.warning.main, fontSize: '0.7rem', fontStyle: 'italic'}} html>{t("coach.consents.no_access")}</Typography>
      </Grid>}
      {!object.consent_status?.is_intake && !consentAsked && <Grid item xs={12}>
        <Button
          label={t("coach.general.buttons.ask_consent")}
          id='ask_consent2'
          contained
          onClick={(v) => {
            mutationCustomAction.mutate({
              formData: {
                object: 'user',
                class_action: 'custom_api_ask_consent',
                email: object?.email,
                practice_id: (login?.practice_id || currentPractice?.id)
              },
              consent: true
            });
          }}
          sx={{marginTop: 1}}
        />
        <Button
          label={t("coach.general.buttons.see_limited_version")}
          id='limited_version'
          size="small"
          text
          onClick={(v) => {
            navigate(`/user/${object.id}/file`, {state: {tab: 0}});
          }}
          sx={{marginTop: 1}}
        />
      </Grid>}
      {!object.consent_status?.is_intake && !!consentAsked && <Grid item xs={12}>
        <Typography html>{t("coach.paragraphs.existing_user_consent_asked")}</Typography>
      </Grid>}

      {/* The 7 orderable stat rows, in the coach's saved order. Hidden items
          are skipped entirely (reachable on the file page). */}
      {!!object.consent_status?.is_intake && orderedItems.map((item) => {
        if (item.state !== 'shown') return null;
        switch (item.key) {
          case 'episode': {
            const episodeDate = (object.episodes || [])[(object.episodes || []).length - 1];
            return <Grid item xs={12} key={item.key}>
              <Typography variant="body2">{t("coach.submenu.detail_episodes.current_episode")}: &#176;{object.episode}</Typography>
              {!!episodeDate && episodeDate !== "..." && <Typography variant="body2">{(episodeDate)}</Typography>}
            </Grid>;
          }
          case 'intake_stats':
            return <Grid item xs={12} key={item.key}>
              {intakeProgressSuccess && <Typography variant="body2" html>{`${t("coach.user_detail.intake_progress", "Completion:")} ${intakeProgress.custom_result}%`}</Typography>}
              {!!object?.intake_profile?.updated_at && <Typography variant="body2" html>{fmtDay(object?.intake_profile?.updated_at)}</Typography>}
              {!!object?.intake_profile_practice?.updated_at && (object?.intake_profile_practice?.updated_at !== object?.intake_profile_practice?.created_at) && <Typography variant="body2" html>{`${t("coach.submenu.intake_profiles.coach_time_updated_short")}: ${fmtDay(object?.intake_profile_practice?.updated_at)}${!!object?.intake_profile_practice?.editor_id ? ` ${t("general.terms.by")} ${object?.intake_profile_practice?.editor_full_name}` : ''}`}</Typography>}
            </Grid>;
          case 'uploads_stats':
            return (!!lastUpload?.date && <Grid item xs={12} key={item.key}>
              <Typography variant="body2" html>{`${t("coach.submenu.detail.latest_upload_dates_from")}${fmtDay(lastUpload.date)}`}</Typography>
            </Grid>);
          case 'timeline_stats':
            return (count > 0 && <Grid item xs={12} key={item.key}>
              <Typography variant="body2" html>
                {`${t("coach.submenu.detail.timeline_entries_count", {count: count})}${!!timelineLastDate ? ` · ${t("coach.submenu.detail.timeline_last_entry_on")}${fmtDay(timelineLastDate)}` : ''}`}
              </Typography>
            </Grid>);
          case 'treatment_plan_stats':
            return (!!treatmentPlan && <Grid item xs={12} key={item.key}>
              <Typography variant="body2" html>{`${t("coach.submenu.treatment_plans.time_updated_short")} ${fmtDay(treatmentPlan.updated_at)}${!!treatmentPlan.editor_id ? ` ${t("general.terms.by")} ${treatmentPlan.editor_full_name}` : ''}`}</Typography>
            </Grid>);
          case 'advices_count':
            return <Grid item xs={12} key={item.key}>
              <Typography variant="body2" html>{t("coach.submenu.detail.advices_count", {count: object?.practice_advices_count || 0})}</Typography>
            </Grid>;
          case 'uploads_count':
            return <Grid item xs={12} key={item.key}>
              <Typography variant="body2" html>{t("coach.submenu.detail.uploads_count", {count: object?.advice_uploads_count || 0})}</Typography>
            </Grid>;
          default:
            return null;
        }
      })}

    </Grid>
  </Paper>;
}
